import React, { forwardRef, useContext, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import RingilDataTable, { ColumnConfig, DateTimeFormatType, FilterConfig, FilterType, PagedData } from '@components/RingilDataTable/RingilDataTable.tsx';
import { ActionOutcomeEnum, AuditActionEnum, AuditActorOutDTO, AuditCategoryEnum, AuditEntryOutDTO, AuditFilter, KindEnum, Pageable, PageResponseAuditEntryOutDTO } from '@api/ringil-api.v2';
import { stringDateTimeSecondsWithLocaleFromApiStringInTz } from '@utils/dateWithTz';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import SvgAuditlog from '@components/ringil3/icons/heading/Auditlog';
import SvgCheckCircle from '@icons/CheckCircle';
import SvgAlertCircle from '@icons/AlertCircle';
import SvgInfoCircle from '@components/framework/icons/InfoCircle.tsx';
import SvgChevron from '@icons/chevron.svg?react';
import COButton from '@components/FigmaComponents/COButton.tsx';
import SvgWarning from '@icons/Warning';
import CustomTooltip from '@components/ringil3/Tooltip/CustomTooltip';
import SafeHtml from '@components/RingilComponents/RichTextEditor/SafeHtml.tsx';
import { RowWithSpaceBetween } from '@components/styles';

// A single change whose before/after text exceeds this wraps past the 2-line clamp in the narrow
// Data change column, so it needs the expandable row. Roughly the chars that fit in two clamped lines.
const LONG_VALUE_CHARS = 40;

// EXPEDITION_CHANGED emits one entry per subject (header / each changed location / each changed order).
// A subject entry leads with one of these params (name is an i18n key) whose value names the subject;
// it's surfaced on the Activity cell, not rendered as a "— → subject" change row.
const SUBJECT_PARAM_NAMES = ['generic.location', 'generic.order'];

export interface AuditLogTableHandle {
  reload: () => void;
  applyFilterValue: (property: string, value: any) => void;
}

// The non-scope filter fields the core reads from the table filter state. The wrapper adds its scope
// (entityType/entityId for one entity, or companyId for the cross-company admin view) and calls the
// matching endpoint, returning the raw 0-based page response.
export interface AuditSharedFilter {
  action?: AuditFilter['action'];
  category?: AuditFilter['category'];
  actionOutcome?: AuditFilter['actionOutcome'];
  actorId?: AuditFilter['actorId'];
  impersonated?: boolean;
  from?: string;
  to?: string;
}

// Sentinel option in the actor dropdown: the BE collapses all impersonated rows into one actor LoV
// entry named IT_OWNER_NAME. We remap its value to this sentinel so picking it filters on the
// `impersonated` boolean instead of an actorId (a masked row may have no actorId at all).
export const IT_OWNER_ACTOR = '__IT_OWNER__';
export const IT_OWNER_NAME = 'IT OWNER'; // BE literal (untranslated) on both actor.name and the LoV entry

interface ComponentProps {
  fetchPage: (pageable: Pageable, shared: AuditSharedFilter, tableFilter: any, onSuccess: (resp: PageResponseAuditEntryOutDTO) => void) => void;
  actorOptions: { label: string; value: string }[];
  filterName: string;
  companyValue?: (row: AuditEntryOutDTO) => string | undefined;
  // Admin overrides to the raw actor (on-behalf lives in its own column); tenant default is on-behalf-first.
  performerOf?: (row: AuditEntryOutDTO) => { user?: AuditActorOutDTO; id?: string };
  // Facet of actions actually present for the entity (labels from BE activityKey); falls back to the
  // full enum when omitted. Actions are the only BE-owned filter; category/outcome are FE (built here).
  actionOptions?: { label: string; value: string }[];
  // Admin surface: show the actor's company under the actor name.
  showActorCompany?: boolean;
  // Secondary block folded under Performed by / Company instead of costing a column of its own (admin
  // uses these for On behalf of / Restricted to). Supplying one also switches that column's header to
  // the combined label, so the header states what the secondary line is.
  performedByExtra?: (row: AuditEntryOutDTO) => React.ReactNode;
  companyExtra?: (row: AuditEntryOutDTO) => React.ReactNode;
  // Admin-only: flag DESCRIPTION params the template never consumes. Untranslated on purpose — it is a
  // key-authoring diagnostic, not user-facing copy.
  showParamDiagnostics?: boolean;
  extraColumns?: ColumnConfig<AuditEntryOutDTO>[];
  extraFilters?: FilterConfig[];
  // Suppress the own "Audit log" headline when a surrounding SectionPanel already provides one.
  hideHeader?: boolean;
}

const AuditLogTable = forwardRef<AuditLogTableHandle, ComponentProps>(
  (
    { fetchPage, actorOptions, filterName, companyValue, performerOf, actionOptions, showActorCompany, performedByExtra, companyExtra, showParamDiagnostics, extraColumns, extraFilters, hideHeader = false },
    ref,
  ) => {
  const { tr } = useTranslationLgs();
  const { i18n } = useTranslation();
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const tableRef = useRef<{ reloadData: () => void; selectedObjects: AuditEntryOutDTO[]; clear: () => void; applyFilterValue: (property: string, value: any) => void }>(null);
  // Impersonated (IT OWNER) rows: the actor is masked, so its company (Ringil s.r.o.) must not show —
  // attribute the row to the company acted for. onBehalfOf is only set on impersonated rows.
  const companyOf = companyValue ?? ((row: AuditEntryOutDTO) => row.onBehalfOfCompanyName ?? row.actorCompanyName ?? row.actorCompanyId);
  const performer = performerOf ?? ((row: AuditEntryOutDTO) => ({ user: row.onBehalfOfUser ?? row.actor, id: row.onBehalfOfUserId ?? row.actorId }));
  // Mirrors RingilDataTable's own expansion state (which it doesn't expose) so the toggle can show
  // the matching chevron. Kept in sync by only ever flipping both together.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  useImperativeHandle(ref, () => ({
    reload: () => tableRef.current?.reloadData(),
    applyFilterValue: (property: string, value: any) => tableRef.current?.applyFilterValue(property, value),
  }));

  // MultiSelect filters hold arrays; drop empties so we never send `[]`.
  const asArray = (v: unknown): string[] | undefined => (Array.isArray(v) && v.length ? (v as string[]) : undefined);

  const loadDataApi = (filter: any, pageable: Pageable, onSuccess: (pagedData: PagedData<AuditEntryOutDTO>) => void) => {
    // The IT OWNER entry lives inside the actor multiselect; picking it sends impersonated=true and is
    // removed from the actorId list (a masked row may have no actorId), OR-combined with any real actors.
    const actorIds = asArray(filter?.actorId);
    const itOwner = !!actorIds?.includes(IT_OWNER_ACTOR);
    const realActorIds = itOwner ? actorIds?.filter(a => a !== IT_OWNER_ACTOR) : actorIds;
    const shared: AuditSharedFilter = {
      action: asArray(filter?.action) as AuditFilter['action'],
      category: asArray(filter?.category) as AuditFilter['category'],
      actionOutcome: asArray(filter?.actionOutcome) as AuditFilter['actionOutcome'],
      actorId: realActorIds?.length ? realActorIds : undefined,
      impersonated: itOwner ? true : undefined,
      from: filter?.from || undefined,
      to: filter?.to || undefined,
    };
    // Paging is 1-based on both sides (useFilter and the endpoint) — pass through, as every other list does.
    fetchPage(pageable, shared, filter, resp => {
      onSuccess({
        data: resp?.data ?? [],
        total: resp?.total ?? 0,
        page: resp?.page ?? pageable.pageNumber,
        size: resp?.size ?? pageable.pageSize,
      });
    });
  };

  const renderUser = (user?: { name?: string; email?: string }, fallback?: string) => {
    if (user?.email) {
      return (
        <a href={`mailto:${user.email}`} className="text-inherit hover:underline">
          {user.name ?? user.email}
        </a>
      );
    }
    return <span>{user?.name ?? fallback ?? tr('AuditLog.system', 'System')}</span>;
  };

  // DESCRIPTION params are the descriptionKey's interpolation values. Expose each under BOTH its name
  // and its position so a template can reference {{module}} or {{0}} (i18next needs double braces).
  const interpolation = (row: AuditEntryOutDTO): Record<string, string> => {
    const values: Record<string, string> = {};
    (row.params ?? [])
      .filter(p => p.kind === KindEnum.DESCRIPTION)
      .forEach(p => {
        const v = p.value ?? '—';
        values[String(p.position)] = v;
        if (p.name) values[p.name] = v;
      });
    return values;
  };

  // `position` is the BE-defined order; the array order isn't guaranteed to match it.
  // Diagnostic, audit surface only and deliberately untranslated: i18next silently drops DESCRIPTION
  // params the template never references, so a wrong key or a stale param set looks fine on screen.
  // Resolved per param, not per placeholder — `interpolation()` publishes each param under BOTH its
  // name and its position, so a param is unused only when NEITHER form appears in the template.
  const unusedDescriptionParams = (row: AuditEntryOutDTO): string[] => {
    const key = row.descriptionKey;
    if (!showParamDiagnostics || !key) return [];
    let template: string | undefined;
    for (const lng of i18n.languages ?? [i18n.language]) {
      const raw = i18n.getResource(lng, 'common', key);
      if (typeof raw === 'string') {
        template = raw;
        break;
      }
    }
    // Unregistered: the raw key is already rendered in place of the sentence — flagging adds nothing.
    if (template === undefined) return [];
    const used = new Set([...template.matchAll(/\{\{\s*([^}\s]+)\s*\}\}/g)].map(m => m[1]));
    return (row.params ?? [])
      .filter(p => p.kind === KindEnum.DESCRIPTION)
      .filter(p => !used.has(String(p.position)) && !(p.name && used.has(p.name)))
      .map(p => p.name ?? `#${p.position}`);
  };

  const isDataChange = (p: { kind: KindEnum }) => p.kind === KindEnum.DATA_CHANGE || p.kind === KindEnum.DATA_CHANGE_KEY;

  // The leading generic.location|generic.order param naming an EXPEDITION_CHANGED subject; shown on the
  // Activity cell instead of as a change row.
  const subjectParam = (row: AuditEntryOutDTO) => (row.params ?? []).find(p => isDataChange(p) && SUBJECT_PARAM_NAMES.includes(p.name ?? ''));

  const dataChanges = (row: AuditEntryOutDTO) =>
    (row.params ?? [])
      .filter(p => isDataChange(p) && !SUBJECT_PARAM_NAMES.includes(p.name ?? ''))
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

  // DATA_CHANGE values are data, shown as-is (may be a " | " separated list). DATA_CHANGE_KEY values are
  // i18n keys — possibly several joined by ", ", each a separate key — so translate each element.
  const displayValue = (kind: KindEnum, raw?: string): string => {
    if (raw == null) return '—';
    return kind === KindEnum.DATA_CHANGE_KEY ? raw.split(', ').map(k => tr(k)).join(', ') : raw;
  };

  // The collapsed cell renders "name: old → new" together and clamps to 2 lines, so the combined length
  // (label included) is what overflows — checking a value alone missed pairs that only wrap together.
  const isLongChange = (p: { kind: KindEnum; name?: string; oldValue?: string; value?: string }) =>
    (p.name ? tr(p.name).length + 2 : 0) + displayValue(p.kind, p.oldValue).length + displayValue(p.kind, p.value).length > LONG_VALUE_CHARS;

  // Note fields carry sanitized rich-text HTML; render it through the note renderer (SafeHtml sanitizes +
  // renders) instead of showing raw tags. Plain values (numbers, plates) keep the inline text layout.
  const isHtmlValue = (v?: string) => !!v && /<[a-z][^>]*>/i.test(v);
  const isHtmlChange = (p: { oldValue?: string; value?: string }) => isHtmlValue(p.oldValue) || isHtmlValue(p.value);

  // Row is expandable when the 2-line collapsed Data change preview can hide content: several stacked
  // params, a value that wraps past the clamp, or a note's rich HTML. The expander lives in the
  // Description column (end of the row); this predicate drives whether it shows.
  const isExpandable = (row: AuditEntryOutDTO) => {
    const changes = dataChanges(row);
    return changes.length > 1 || changes.some(isLongChange) || changes.some(isHtmlChange);
  };

  const toggleExpansion = (row: AuditEntryOutDTO, toggleExpandedRow?: (row: AuditEntryOutDTO) => void) => {
    toggleExpandedRow?.(row);
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(row.id)) next.delete(row.id);
      else next.add(row.id);
      return next;
    });
  };

  // Activity = the BE-supplied `activityKey` (its label owns its own translation; no FE map). Rendered
  // without params on purpose — the interpolated text is the Data change / Description column. Last
  // resort when a row carries no key: the raw `auditAction`.
  const activityOf = (row: AuditEntryOutDTO) => (row.activityKey ? tr(row.activityKey) : row.auditAction);

  // Outcome shown as an icon in front of the activity (replaces the dedicated Outcome column). Icons
  // carry their own semantic colour (green/red/grey), so no className tinting.
  const outcomeIcon = (row: AuditEntryOutDTO) => {
    switch (row.actionOutcome) {
      case ActionOutcomeEnum.OK:
        return <SvgCheckCircle />;
      case ActionOutcomeEnum.ERROR:
        return <SvgAlertCircle />;
      default:
        return <SvgInfoCircle />;
    }
  };
  // Small, stable enum→English maps — the tr() default for the FE `AuditLog.*Option.*` value keys
  // (standard FE pattern). Actions are NOT here: they grow, so their label comes from BE `activityKey`.
  const outcomeLabel: Record<ActionOutcomeEnum, string> = {
    [ActionOutcomeEnum.INFO]: 'Info',
    [ActionOutcomeEnum.OK]: 'OK',
    [ActionOutcomeEnum.ERROR]: 'Error',
  };
  const categoryLabel: Record<AuditCategoryEnum, string> = {
    [AuditCategoryEnum.USER_ACTION]: 'User action',
    [AuditCategoryEnum.INTEGRATION]: 'Integration',
  };
  // Each enum owns one namespace: `AuditLog.<enum>.name` is the label, `AuditLog.<enum>.<VALUE>` the
  // value. The parent is never read as a string, so no object-vs-string collision.
  const outcomeLabelOf = (o?: ActionOutcomeEnum) => (o ? tr(`AuditLog.outcome.${o}`, outcomeLabel[o]) : '');
  const categoryFilterOptions = Object.values(AuditCategoryEnum).map(c => ({ label: tr(`AuditLog.category.${c}`, categoryLabel[c]), value: c }));
  const outcomeFilterOptions = Object.values(ActionOutcomeEnum).map(o => ({ label: tr(`AuditLog.outcome.${o}`, outcomeLabel[o]), value: o }));

  const coreColumns: ColumnConfig<AuditEntryOutDTO>[] = [
    {
      id: 'activity',
      field: 'auditAction',
      header: 'AuditLog.activity',
      headerDefault: 'Activity',
      order: 10,
      width: '12rem',
      render: row => {
        const subj = subjectParam(row);
        return (
          <div className="flex items-start gap-2">
            <span className="shrink-0" title={outcomeLabelOf(row.actionOutcome)}>{outcomeIcon(row)}</span>
            {/* Long unbreakable enum labels (non-live entity types) must wrap, not overflow the column. */}
            <div className="min-w-0 flex flex-col">
              <span className="break-words">{activityOf(row)}</span>
              {subj && <span className="text-xs text-content-secondary break-words">{displayValue(subj.kind, subj.value)}</span>}
            </div>
          </div>
        );
      },
    },
    {
      id: 'dateTime',
      header: 'AuditLog.dateTime',
      headerDefault: 'Date and time',
      order: 20,
      width: '8.5rem',
      render: row => (
        <div className="flex-col">
          <div>{stringDateTimeSecondsWithLocaleFromApiStringInTz(row.atUtc, loggedUser.defaultTimezoneId)}</div>
          <COTimezone timezoneId={loggedUser.defaultTimezoneId} />
        </div>
      ),
    },
    {
      id: 'performedBy',
      header: 'AuditLog.performedBy',
      headerDefault: 'Performed by',
      // Composed from the two EXISTING keys rather than a new combined one — `headerRender` bypasses
      // tr(), so no "Performed by / On behalf of" key gets saveMissing-registered into Locize.
      headerRender: performedByExtra ? () => `${tr('AuditLog.performedBy', 'Performed by')} / ${tr('AuditLog.onBehalfOf', 'On behalf of')}` : undefined,
      order: 30,
      width: performedByExtra ? '10.5rem' : '8.5rem',
      render: row => {
        const { user, id } = performer(row);
        return (
          <div className="flex-col">
            {renderUser(user, id)}
            {showActorCompany && row.actorCompanyName && <div className="text-xs text-content-secondary">{row.actorCompanyName}</div>}
            {performedByExtra?.(row)}
          </div>
        );
      },
    },
    {
      id: 'company',
      header: 'AuditLog.company',
      headerDefault: 'Company',
      headerRender: companyExtra ? () => `${tr('AuditLog.company', 'Company')} / ${tr('AuditLog.restrictedTo', 'Restricted to')}` : undefined,
      order: 35,
      width: companyExtra ? '11rem' : '9rem',
      render: row => (
        <div className="flex-col">
          <div className="break-words">{companyOf(row)}</div>
          {companyExtra?.(row)}
        </div>
      ),
    },
    {
      id: 'dataChange',
      header: 'AuditLog.dataChange',
      headerDefault: 'Data change',
      order: 40,
      width: '13rem',
      render: row => {
        const changes = dataChanges(row);
        if (!changes.length) return null;
        // Collapsed preview only: the whole cell clamps to 2 text lines with an ellipsis. The full
        // before/after grid is reached via the expander chevron at the end of the Description column.
        return (
          <div className="line-clamp-2 break-words">
            {changes.map((p, i) => (
              // Divider + spacing so consecutive changes don't run together when several wrap.
              <div key={i} className={i > 0 ? 'mt-1.5 pt-1.5 border-t border-content-quaternary' : ''}>
                {/* Field label (i18n key) so a generic activity like "Basic info updated" still says WHICH field changed. */}
                {p.name && <span className="text-content-secondary">{tr(p.name)}: </span>}
                {isHtmlChange(p) ? (
                  <>
                    <SafeHtml html={p.oldValue ?? '—'} />
                    <span className="mx-1 text-content-secondary">→</span>
                    <SafeHtml html={p.value ?? '—'} />
                  </>
                ) : (
                  <>
                    <span>{displayValue(p.kind, p.oldValue)}</span>
                    <span className="mx-1">→</span>
                    <span>{displayValue(p.kind, p.value)}</span>
                  </>
                )}
              </div>
            ))}
          </div>
        );
      },
    },
    {
      id: 'description',
      header: 'AuditLog.description',
      headerDefault: 'Description',
      order: 50,
      // Widest column by design: long free-text descriptions / filenames / raw payloads land here.
      // An explicit large width (not width-less) is required — the table is auto-layout, so a width-less
      // column doesn't pool the slack, it just collapses to content width.
      width: '26rem',
      // Two independent parts, concatenated in a fixed order so no combination can lose content:
      // `description` is untranslatable free text (a filename, a raw payload) and comes first; then the
      // translatable sentence from `descriptionKey` + its DESCRIPTION params. Either, both, or neither.
      render: (row, toggleExpandedRow) => {
        const unused = unusedDescriptionParams(row);
        const expandable = isExpandable(row);
        if (!row.description && !row.descriptionKey && !unused.length && !expandable) return null;
        const warnId = `audit-unused-params-${row.id}`;
        return (
          <div className="flex items-start gap-2">
            <div className="flex-col min-w-0 flex-1">
              {row.description && <div className="break-words">{row.description}</div>}
              {row.descriptionKey && <div className="break-words">{tr(row.descriptionKey, row.descriptionKey, interpolation(row))}</div>}
              {unused.length > 0 && (
                <>
                  <span id={warnId} className="mt-1 w-fit">
                    <SvgWarning />
                  </span>
                  <CustomTooltip targetSelector={`#${warnId}`} content={`Unused description params: ${unused.join(', ')}`} />
                </>
              )}
            </div>
            {/* Expander for the full before/after data-change grid — moved here from the Data change column.
                Same COButton + rotating chevron as the SectionPanel reveal, for a consistent affordance. */}
            {expandable && (
              <COButton
                onClick={() => toggleExpansion(row, toggleExpandedRow)}
                icon={<SvgChevron className={`w-4 h-4 text-content-brand ${expandedIds.has(row.id) ? 'rotate-180' : ''}`} />}
              />
            )}
          </div>
        );
      },
    },
  ];

  // Filter labels reuse the column's canonical key (no separate `filter*` key). From/To are generic.
  const coreFilters: FilterConfig[] = [
    {
      filterType: FilterType.MultiSelect,
      label: tr('AuditLog.activity', 'Activity'),
      placeholder: tr('AuditLog.activity', 'Activity'),
      value: ['action'],
      // Facet of present actions (labelled by BE activityKey); raw enum when the facet isn't supplied.
      options: (actionOptions ?? Object.values(AuditActionEnum).map(a => ({ label: a, value: a }))).map(o => ({ ...o, active: true })),
    },
    {
      filterType: FilterType.MultiSelect,
      // `.name` = the field label; sibling `AuditLog.category.<VALUE>` = the option labels.
      label: tr('AuditLog.category.name', 'Category'),
      placeholder: tr('AuditLog.category.name', 'Category'),
      value: ['category'],
      // Never in the default set (both views) — addable via "+ Add filter".
      defaultShown: false,
      options: categoryFilterOptions.map(o => ({ ...o, active: true })),
    },
    {
      filterType: FilterType.MultiSelect,
      label: tr('AuditLog.outcome.name', 'Outcome'),
      placeholder: tr('AuditLog.outcome.name', 'Outcome'),
      value: ['actionOutcome'],
      options: outcomeFilterOptions.map(o => ({ ...o, active: true })),
    },
    {
      filterType: FilterType.MultiSelect,
      label: tr('AuditLog.performedBy', 'Performed by'),
      placeholder: tr('AuditLog.performedBy', 'Performed by'),
      value: ['actorId'],
      // The IT OWNER entry is remapped (upstream) to the sentinel; loadDataApi turns it into impersonated=true.
      options: actorOptions.map(o => ({ ...o, active: true })),
    },
    {
      filterType: FilterType.Calendar,
      label: tr('generic.from', 'From'),
      placeholder: tr('generic.from', 'From'),
      value: ['from'],
      dateTimeFormat: DateTimeFormatType.ISOString,
    },
    {
      filterType: FilterType.Calendar,
      label: tr('generic.to', 'To'),
      placeholder: tr('generic.to', 'To'),
      value: ['to'],
      // Keep To in the default set even on admin, where the two extra columns push it past the 7-filter
      // positional cut-off. (Customer already has it in the primary set by position.)
      defaultShown: true,
      dateTimeFormat: DateTimeFormatType.ISOString,
    },
  ];

  // Full before/after for values too long to sit in the cell. One row per change across the full table
  // width: field name in a left gutter, then `old → new` inline (wrapping only when a value is very
  // long). `name` is an i18n key (both data-change kinds). Language-neutral `→`, so no i18n keys here.
  const rowExpansionTemplate = (row: AuditEntryOutDTO) => {
    const changes = dataChanges(row);
    return (
      <div className="grid grid-cols-[minmax(6rem,10rem)_1fr] gap-x-6 gap-y-2 p-4 rounded-lg bg-base-tertiary">
        {changes.map((p, i) => (
          <React.Fragment key={i}>
            <div className="text-xs text-content-secondary break-words pt-0.5">{p.name ? tr(p.name) : ''}</div>
            {isHtmlChange(p) ? (
              // HTML notes span multiple lines; arrow on its own row keeps old vs new distinct.
              <div className="flex flex-col gap-1">
                <SafeHtml html={p.oldValue ?? '—'} />
                <div className="text-content-secondary">→</div>
                <SafeHtml html={p.value ?? '—'} />
              </div>
            ) : (
              <div className="flex flex-wrap items-baseline gap-x-2">
                <span className="break-words whitespace-pre-wrap">{displayValue(p.kind, p.oldValue)}</span>
                <span className="text-content-secondary">→</span>
                <span className="break-words whitespace-pre-wrap">{displayValue(p.kind, p.value)}</span>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const columnConfig = [...coreColumns, ...(extraColumns ?? [])];
  const filtersConf = [...(extraFilters ?? []), ...coreFilters];

  return (
    <div className="py-6">
      {!hideHeader && (
        <RowWithSpaceBetween className="mb-6">
          <COHeadline variant={'h2'} title={tr(`AuditLog.heading`, `Audit log`)} icon={<SvgAuditlog />} />
        </RowWithSpaceBetween>
      )}
      {/* 7+ columns exceed the container; scroll horizontally instead of clipping the last column. */}
      <div className="overflow-x-auto">
        <RingilDataTable
          ref={tableRef}
          columnConfig={columnConfig}
          filtersConf={filtersConf}
          loadDataFromApi={loadDataApi}
          filterName={filterName}
          rowExpansionTemplate={rowExpansionTemplate}
        />
      </div>
    </div>
  );
});

AuditLogTable.displayName = 'AuditLogTable';

export default AuditLogTable;
